import { Button } from '@/components/ui/button';
import type { DashboardListData } from '@pm-next/shared-types';
import { humanizeLabel } from '@pm-next/shared-types';

interface ListBlockProps {
  data: DashboardListData;
  onPageChange?: (page: number) => void;
}

export function ListBlock({ data, onPageChange }: ListBlockProps) {
  const totalPages = Math.max(1, Math.ceil(data.total / data.pageSize));
  const columns = data.columns.length > 0 ? data.columns : ['value'];

  return (
    <div className="flex h-full min-h-0 flex-col gap-2">
      <div className="min-h-0 flex-1 overflow-auto">
        <table className="w-full text-left text-sm">
          <thead className="sticky top-0 bg-card">
            <tr className="border-b text-muted-foreground">
              {columns.map((col) => (
                <th key={col} className="px-2 py-1.5 font-medium">
                  {humanizeLabel(col)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.rows.length === 0 ? (
              <tr>
                <td
                  colSpan={columns.length}
                  className="px-2 py-6 text-center text-muted-foreground"
                >
                  No rows
                </td>
              </tr>
            ) : (
              data.rows.map((row, i) => (
                <tr key={i} className="border-b border-border/60">
                  {columns.map((col) => (
                    <td key={col} className="px-2 py-1.5 align-top">
                      {data.formattedRows?.[i]?.[col] ?? formatCell(row[col])}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      {totalPages > 1 && (
        <div className="flex items-center justify-between gap-2 text-xs text-muted-foreground">
          <span>
            Page {data.page} of {totalPages} ({data.total} rows)
          </span>
          <div className="flex gap-1">
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={data.page <= 1}
              onClick={() => onPageChange?.(data.page - 1)}
            >
              Prev
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={data.page >= totalPages}
              onClick={() => onPageChange?.(data.page + 1)}
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function formatCell(value: unknown): string {
  if (value == null) return '—';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}
